import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import {
  ArrowDown,
  ArrowRight,
  ArrowUpRight,
  CloudRainWind,
  Flame,
  HeartHandshake,
  Wind,
  Leaf,
  Footprints,
} from "lucide-react";
import Reveal, { MaskLines } from "../components/Reveal";
import Marquee from "../components/Marquee";
import ProductModal from "../components/ProductModal";
import CheckoutModal from "../components/CheckoutModal";
import { PRODUCTS } from "../data/products";
import { useBooking } from "../lib/booking";
import { IMAGES, TOPICS, STORY_CAMPS, PRINCIPLES, FOUNDERS, OFFERINGS } from "../data/content";

const TOPIC_ICONS = { cloud: CloudRainWind, flame: Flame, heart: HeartHandshake, wind: Wind };

const PRODUCT_IMAGES = ["journalFlatlay", "headphones", "audioPerson", "journalFlatlay"];

export default function Home() {
  const { openBooking } = useBooking();
  const heroRef = useRef(null);
  const products = PRODUCTS.slice(0, 3);
  const [preview, setPreview] = useState(null);
  const [buying, setBuying] = useState(null);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.06, 1.2]);
  const contentFade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const badgeX = useSpring(mx, { stiffness: 45, damping: 18 });
  const badgeY = useSpring(my, { stiffness: 45, damping: 18 });

  const onHeroMouse = (e) => {
    const r = heroRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 24);
    my.set(((e.clientY - r.top) / r.height - 0.5) * 18);
  };

  const scrollToStory = () => {
    const el = document.querySelector("#story");
    if (!el) return;
    if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70 });
    else el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div>
      {/* ---------------- HERO ---------------- */}
      <section
        ref={heroRef}
        onMouseMove={onHeroMouse}
        data-testid="hero-section"
        className="relative flex min-h-[92vh] items-end overflow-hidden bg-pinedeep"
      >
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt="Mist settling over a quiet mountain forest"
            className="h-full w-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-pinedeep/90 via-pinedeep/35 to-pinedeep/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-pinedeep/70 via-transparent to-transparent" />

        <motion.div
          style={{ x: badgeX, y: badgeY }}
          className="absolute right-[7%] top-[16%] hidden lg:block"
        >
          <button
            data-testid="hero-story-badge"
            onClick={scrollToStory}
            className="group rounded-3xl border border-cream/25 bg-cream/10 px-5 py-4 text-left backdrop-blur-md transition-colors duration-300 hover:bg-cream/20"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-cream/70">
              4,100 m · The Summit
            </p>
            <p className="mt-1 font-serif text-lg italic text-cream">
              Our story starts with a hello
            </p>
            <p className="mt-1 flex items-center gap-1.5 text-xs text-cream/70">
              Walk up with us
              <ArrowDown className="h-3 w-3 transition-transform duration-300 group-hover:translate-y-0.5" />
            </p>
          </button>
        </motion.div>

        <motion.div
          style={{ opacity: contentFade }}
          className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-20 pt-40 sm:px-8"
        >
          <div className="grid items-end gap-10 lg:grid-cols-[1.6fr_1fr]">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.15 }}
                className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-blush"
              >
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-terra animate-pulse-soft" />
                Back of the Mind · online therapy & wellness
              </motion.p>
              <MaskLines
                lines={["Look inward.", "Understand", "what shapes you."]}
                delay={0.35}
                className="mt-6 font-serif text-[13vw] leading-[1.02] tracking-tight text-cream sm:text-7xl lg:text-[5.6rem]"
              />
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="lg:pb-3"
            >
              <p className="max-w-md text-base leading-relaxed text-cream/85 sm:text-lg">
                Online therapy, corporate wellness and digital tools for the thoughts
                quietly shaping your days. Warm, practical, and entirely at your own pace.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  data-testid="hero-book-session-button"
                  onClick={() => openBooking()}
                  className="group flex items-center gap-2 rounded-full bg-cream px-7 py-3.5 text-sm font-semibold text-pinedeep transition-all duration-300 hover:-translate-y-0.5 hover:bg-blush hover:shadow-xl"
                >
                  Book a Session
                  <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                </button>
                <Link
                  data-testid="hero-explore-tools-button"
                  to="/store"
                  className="flex items-center gap-2 rounded-full border border-cream/40 px-7 py-3.5 text-sm font-semibold text-cream backdrop-blur-sm transition-all duration-300 hover:-translate-y-0.5 hover:border-cream hover:bg-cream/10"
                >
                  Explore Digital Tools
                </Link>
              </div>
            </motion.div>
          </div>
        </motion.div>

        <button
          data-testid="hero-scroll-cue"
          onClick={scrollToStory}
          aria-label="Scroll to our story"
          className="absolute bottom-6 left-1/2 z-10 -translate-x-1/2 rounded-full border border-cream/25 p-3 text-cream/80 animate-floaty"
        >
          <ArrowDown className="h-4 w-4" />
        </button>
      </section>

      <Marquee />

      {/* ---------------- OUR STORY ---------------- */}
      <section id="story" data-testid="story-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
        <div className="grid gap-16 lg:grid-cols-[0.85fr_1.15fr]">
          <div className="lg:sticky lg:top-28 lg:self-start">
            <Reveal>
              <p className="eyebrow">Our story</p>
              <h2 className="mt-4 font-serif text-4xl leading-[1.12] tracking-tight text-ink sm:text-5xl">
                Some climbs are made alone.{" "}
                <span className="italic text-pine">This one, we made laughing.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-6 max-w-md leading-relaxed text-bodytext">
                Back of the Mind began on a quiet trail between two friends: one calm as
                a river, one light as a breeze. Take your time. Walk up the mountain with
                us — breathe in… and out. Then begin.
              </p>
            </Reveal>
            <Reveal delay={0.2} className="mt-10">
              <div className="overflow-hidden rounded-[2.5rem] shadow-[0_24px_60px_rgba(45,90,70,0.16)]">
                <img
                  src={IMAGES.story}
                  alt="Mist drifting through a mountain forest"
                  loading="lazy"
                  className="h-72 w-full object-cover sm:h-80"
                />
              </div>
            </Reveal>
          </div>

          <ol className="relative space-y-14 border-l border-pine/15 pl-8 sm:pl-12">
            {STORY_CAMPS.map((c, i) => (
              <li key={c.camp} className="relative">
                <span className="absolute -left-[2.15rem] top-2 flex h-4 w-4 items-center justify-center sm:-left-[3.15rem]">
                  <span className="h-2.5 w-2.5 rounded-full bg-terra ring-4 ring-cream" />
                </span>
                <Reveal delay={i * 0.04}>
                  <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-sage">
                    {c.camp}
                  </p>
                  <h3 className="mt-3 font-serif text-2xl text-ink sm:text-3xl">{c.title}</h3>
                  <p className="mt-4 max-w-xl leading-relaxed text-bodytext">{c.text}</p>
                </Reveal>
              </li>
            ))}
          </ol>
        </div>

        <div className="mt-28">
          <Reveal>
            <p className="eyebrow">What our friendship taught us</p>
            <h3 className="mt-4 max-w-2xl font-serif text-3xl leading-snug text-ink sm:text-4xl">
              Four small things we carry down the mountain.
            </h3>
          </Reveal>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {PRINCIPLES.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08}>
                <div className="card-soft h-full p-7" data-testid={`story-principle-${i}`}>
                  <p className="font-serif text-3xl italic text-terra">{p.n}</p>
                  <h4 className="mt-4 font-serif text-xl text-ink">{p.title}</h4>
                  <p className="mt-3 text-sm leading-relaxed text-bodytext">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-20 grid gap-5 md:grid-cols-2">
          {FOUNDERS.map((f, i) => (
            <Reveal key={f.persona} delay={i * 0.1}>
              <div
                data-testid={`story-founder-${i}`}
                className="h-full rounded-3xl border border-pine/10 bg-sagelight/50 p-8"
              >
                <p className="font-serif text-2xl italic text-pine">{f.persona}</p>
                <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.2em] text-sage">
                  {f.creds}
                </p>
                <p className="mt-4 leading-relaxed text-bodytext">{f.text}</p>
              </div>
            </Reveal>
          ))}
          <p className="md:col-span-2 text-center text-sm italic text-bodytext/70">
            A river gives depth. A breeze gives lightness. People need both.
          </p>
        </div>

        <Reveal className="mt-16 flex flex-wrap justify-center gap-4">
          <button
            data-testid="story-cta-journey"
            onClick={() => openBooking()}
            className="rounded-full bg-pine px-8 py-4 text-sm font-semibold text-cream transition-all duration-300 hover:-translate-y-0.5 hover:bg-pinedeep hover:shadow-xl"
          >
            Begin your journey
          </button>
          <Link
            data-testid="story-cta-quickheals"
            to="/quick-heals"
            className="flex items-center gap-2 rounded-full border border-pine/25 px-8 py-4 text-sm font-semibold text-pine transition-all duration-300 hover:-translate-y-0.5 hover:bg-sagelight/60"
          >
            <Footprints className="h-4 w-4" /> Explore Quick Heals
          </Link>
        </Reveal>
      </section>

      {/* ---------------- TOPICS ---------------- */}
      <section className="bg-creamdeep py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow">Start here</p>
              <h2 className="mt-4 font-serif text-4xl leading-tight tracking-tight text-ink sm:text-5xl">
                What's loud right now?
              </h2>
              <p className="mt-4 max-w-lg text-bodytext">
                Pick the topic that hums the loudest — each one opens a shelf of
                insights written for exactly that feeling.
              </p>
            </div>
            <Link
              data-testid="topics-view-all-link"
              to="/insights"
              className="group flex items-center gap-2 text-sm font-semibold text-pine"
            >
              Browse all insights
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>

          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {TOPICS.map((t, i) => {
              const Icon = TOPIC_ICONS[t.icon] || Leaf;
              return (
                <Reveal key={t.name} delay={i * 0.07}>
                  <Link
                    to={`/insights?category=${encodeURIComponent(t.name)}`}
                    data-testid={`topic-card-${i}`}
                    className="group block h-full rounded-[2rem] bg-blush p-8 transition-all duration-400 hover:-translate-y-1.5 hover:bg-blushdeep hover:shadow-[0_20px_44px_rgba(196,109,78,0.18)]"
                  >
                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/70 text-terra transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="mt-8 font-serif text-2xl text-ink">{t.name}</h3>
                    <p className="mt-2 text-sm text-bodytext/80">{t.blurb}</p>
                    <p className="mt-6 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-terra">
                      Read the insights
                      <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </p>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- OFFERINGS ---------------- */}
      <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
        <Reveal>
          <p className="eyebrow">Ways we walk with you</p>
          <h2 className="mt-4 font-serif text-4xl leading-tight tracking-tight text-ink sm:text-5xl">
            Three ways to feel lighter.
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {OFFERINGS.map((o, i) => (
            <Reveal key={o.title} delay={i * 0.09}>
              <Link
                to={o.link}
                data-testid={`offering-card-${i}`}
                className="card-soft group block h-full overflow-hidden"
              >
                <div className="overflow-hidden">
                  <img
                    src={IMAGES[o.image]}
                    alt={o.title}
                    loading="lazy"
                    className="h-52 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-7">
                  <h3 className="font-serif text-2xl text-ink">{o.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-bodytext">{o.text}</p>
                  <p className="mt-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-pine">
                    {o.cta}
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                  </p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ---------------- DIGITAL PRODUCTS TEASER ---------------- */}
      <section data-testid="products-teaser" className="border-y border-pine/10 bg-sagelight/40 py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="eyebrow">Digital tools</p>
              <h2 className="mt-4 font-serif text-4xl leading-tight tracking-tight text-ink sm:text-5xl">
                Tools for the back of your mind.
              </h2>
              <p className="mt-4 max-w-lg text-bodytext">
                Guided workbooks, audio companions and pocket decks — built from our
                sessions, yours the moment you check out.
              </p>
            </div>
            <Link
              data-testid="products-teaser-store-link"
              to="/store"
              className="group flex items-center gap-2 text-sm font-semibold text-pine"
            >
              Visit the store
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {products.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.08}>
                <div className="card-soft flex h-full flex-col overflow-hidden" data-testid={`teaser-product-${i}`}>
                  <div className="overflow-hidden">
                    <img
                      src={IMAGES[PRODUCT_IMAGES[i % PRODUCT_IMAGES.length]]}
                      alt={p.name}
                      loading="lazy"
                      className="h-44 w-full object-cover"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-sage">
                      {p.kind}
                    </p>
                    <h3 className="mt-2 font-serif text-2xl text-ink">{p.name}</h3>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-bodytext line-clamp-3">
                      {p.description}
                    </p>
                    <div className="mt-6 flex items-center justify-between">
                      <p className="font-serif text-2xl text-ink">${p.price.toFixed(0)}</p>
                      <button
                        data-testid={`teaser-preview-${i}`}
                        onClick={() => setPreview(p)}
                        className="rounded-full border border-pine/25 px-5 py-2.5 text-xs font-semibold text-pine transition-all hover:bg-pine hover:text-cream"
                      >
                        Preview
                      </button>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- SOS TEASER ---------------- */}
      <section className="relative overflow-hidden bg-pine py-24 text-cream lg:py-28">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-12 px-5 sm:px-8 lg:flex-row lg:justify-between">
          <Reveal className="max-w-xl text-center lg:text-left">
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-blush">
              Quick Heals · free, always
            </p>
            <h2 className="mt-4 font-serif text-4xl leading-tight sm:text-5xl">
              In a spiral right now?
            </h2>
            <p className="mt-4 text-cream/80">
              Two minutes, five senses, zero sign-up. The SOS grounding tool walks you
              back into the room — one sense at a time.
            </p>
            <Link
              data-testid="sos-teaser-link"
              to="/quick-heals"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-cream px-8 py-4 text-sm font-semibold text-pinedeep transition-all duration-300 hover:-translate-y-0.5 hover:bg-blush"
            >
              Try the 2-minute reset
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative flex h-56 w-56 items-center justify-center" aria-hidden="true">
              <motion.span
                animate={{ scale: [1, 1.25, 1], opacity: [0.5, 0.8, 0.5] }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                className="absolute h-40 w-40 rounded-full bg-sagelight/30"
              />
              <motion.span
                animate={{ scale: [1, 1.18, 1], opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                className="absolute h-24 w-24 rounded-full bg-cream/40"
              />
              <span className="relative font-serif text-xl italic">breathe</span>
            </div>
          </Reveal>
        </div>
      </section>

      <ProductModal product={preview} onClose={() => setPreview(null)} onBuy={(p) => { setPreview(null); setBuying(p); }} />
      <CheckoutModal product={buying} onClose={() => setBuying(null)} />
    </div>
  );
}
